import { useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { useMatches } from "@/hooks/useMatches";
import { LIVE_STATUSES } from "@/contants";
import { formatCorrectScoresForGrid, splitCorrectScoresListToSections } from "@/utils/matchInfo";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import BetKbvMatchMinute from "../../BetKbvMatchMinute";
import BetOKOtherBetsDropdown from "../BetOKOtherBetsDropdown/BetOKOtherBetsDropdown";
import ViuGoTipsAiModal from "../../ViuGoTipsAiModal";
import "./index.css";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function BetOKMatch({ match }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const handleOtherOddsClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;
    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const goalOddEvenOdds = otherOddsData?.goalOddEvenOdds ?? [];
  const correctScoresListFull = otherOddsData?.correctScoresOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
    : [];
  const correctScoresListHalf = otherOddsData?.correctScoresHalfOdds
    ? formatCorrectScoresForGrid(otherOddsData.correctScoresHalfOdds)
    : [];
  const correctScoresFull = splitCorrectScoresListToSections(
    correctScoresListFull,
  );
  const correctScoresHalf = splitCorrectScoresListToSections(
    correctScoresListHalf,
  );
  const totalCornersOdds = Array.isArray(otherOddsData?.totalCornersOdds)
    ? otherOddsData.totalCornersOdds
    : [];
  const totalCornersHalfOdds = Array.isArray(otherOddsData?.totalCornersHalfOdds)
    ? otherOddsData.totalCornersHalfOdds
    : [];
  const hasOtherData =
    goalOddEvenOdds?.length > 0 ||
    correctScoresListFull.length > 0 ||
    totalCornersOdds.length > 0 ||
    correctScoresListHalf.length > 0 ||
    totalCornersHalfOdds.length > 0;

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match?.status ?? 0);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;
  const showScore =
    currentHomeScore !== null && currentAwayScore !== null;

  const getTrendColor = (trend, defaultColor = "") => {
    const isUp =
      trend === "up" || trend === "increased" || trend === 1 || trend === "1";
    const isDown =
      trend === "down" || trend === "decreased" || trend === 2 || trend === "2";
    if (isUp) return "kbv-text-trend-up";
    if (isDown) return "kbv-text-trend-down";
    return defaultColor;
  };

  const renderTrendArrow = (trend) => {
    const isUp =
      trend === "up" || trend === "increased" || trend === 1 || trend === "1";
    const isDown =
      trend === "down" || trend === "decreased" || trend === 2 || trend === "2";
    if (isUp)
      return (
        <span
          className="shrink-0 inline-block w-[10px] h-[10px] lu-odds-status--up ml-0.5"
          aria-hidden="true"
        />
      );
    if (isDown)
      return (
        <span
          className="shrink-0 inline-block w-[10px] h-[10px] lu-odds-status--down ml-0.5"
          aria-hidden="true"
        />
      );
    return null;
  };

  const TZ = "Asia/Ho_Chi_Minh";
  const matchTimeStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("HH:mm")
      : "--";
  const matchDateStr =
    match?.matchTime != null
      ? dayjs.unix(match.matchTime).tz(TZ).format("DD/MM")
      : "--";

  const derivedLeagueName = match?.league?.name || match?.leagueName || "";
  const derivedLeagueLogo = match?.league?.logo || match?.leagueLogo || "";
  const homeName = match?.homeTeam?.nameVi || match?.homeName;
  const awayName = match?.awayTeam?.nameVi || match?.awayName;
  const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;
  const modalStatusLabel = !isLive ? "Sắp diễn ra" : "Trực tiếp";

  return (
    <div className="flex flex-col overflow-hidden bg-[var(--theme-bg)] border-b border-[var(--theme-border)]/10 last:border-0">
      <div className="item-schedule-sport kbv-match-row bg-transparent md:h-[68px] flex md:flex-row flex-col items-center md:px-2 md:py-3 p-4 md:gap-x-[5px] gap-x-0 cursor-pointer">
        <div className="flex w-full min-w-0 md:min-w-0 md:w-[32%] md:max-w-[32%] md:flex-none md:shrink-0 flex-row items-center justify-start md:pl-0 gap-x-[10px]">
          {/* Match Time / Status */}
          <div
            className={`flex justify-center items-center md:text-md text-xs md:w-auto w-[70px] shrink-0 gap-x-[3px] md:gap-x-1 md:gap-y-1 text-[var(--theme-text)] ${isLive ? "flex-col" : "flex-nowrap md:flex-col"}`}
          >
            {isLive ? (
              <>
                <span className="flex items-center justify-center gap-1.5 text-red-500 shrink-0 font-bold">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                  Live
                </span>
                <BetKbvMatchMinute
                  liveScore={liveScore}
                  status={statusNum}
                  matchTime={match?.matchTime}
                  halfStartTime={match?.halfStartTime}
                  className="text-[var(--theme-primary)] text-xs md:text-md font-bold shrink-0"
                />
              </>
            ) : (
              <>
                <span className="font-bold">{matchTimeStr}</span>
                <span className="block md:hidden">-</span>
                <span className="font-semi-bold opacity-30">{matchDateStr}</span>
              </>
            )}
          </div>

          {/* Teams (Home vs Away) */}
          <div className="flex flex-1 flex-col justify-center gap-y-[4px] min-w-0 md:max-w-[180px] overflow-hidden">
            <div className="flex h-7 items-center justify-between gap-x-2 min-w-0 w-full">
              <div className="flex items-center justify-start gap-x-2 min-w-0 flex-1 overflow-hidden">
                <div className="overflow-hidden bg-transparent w-6 h-6 md:w-7 md:h-7 shrink-0">
                  <img
                    className="set-img w-full h-full object-cover"
                    src={homeLogo || ""}
                    alt=""
                    loading="lazy"
                  />
                </div>
                <p className="text-[var(--theme-text)] text-xs md:text-sm font-semi-bold truncate min-w-0 leading-none">
                  {match?.homeTeam?.nameVi || match?.homeName}
                </p>
              </div>
              {showScore && (
                <span
                  className={`font-bold text-[var(--theme-text)] md:text-base text-sm shrink-0 tabular-nums ml-2 leading-none${statusNum > 0 ? "" : " md:hidden"}`}
                >
                  {currentHomeScore}
                </span>
              )}
            </div>

            <div className="flex h-7 items-center justify-between gap-x-2 min-w-0 w-full">
              <div className="flex items-center justify-start gap-x-2 min-w-0 flex-1 overflow-hidden">
                <div className="overflow-hidden bg-transparent w-6 h-6 md:w-7 md:h-7 shrink-0">
                  <img
                    className="set-img w-full h-full object-cover"
                    src={awayLogo || ""}
                    alt=""
                    loading="lazy"
                  />
                </div>
                <p className="text-[var(--theme-text)] text-xs md:text-sm font-semi-bold truncate min-w-0 leading-none">
                  { match?.awayTeam?.nameVi || match?.awayName}
                </p>
              </div>
              {showScore && (
                <span
                  className={`font-bold text-[var(--theme-text)] md:text-base text-sm shrink-0 tabular-nums ml-2 leading-none${statusNum > 0 ? "" : " md:hidden"}`}
                >
                  {currentAwayScore}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-row items-center md:gap-x-[5px] gap-x-1 md:mt-0 mt-3 w-full md:w-auto min-w-0">
          {/* Column indicator: FT/HT Labels */}
          <div className="hidden md:flex flex-col md:gap-y-[4px] w-6 shrink-0 text-[10px] font-bold opacity-40 justify-center">
            <div className="h-7 flex items-center justify-center text-[var(--theme-text)] text-base">FT</div>
            <div className="h-7 flex items-center justify-center text-[var(--theme-text)] text-base">HT</div>
          </div>

          {/* Handicap FT/HT */}
          <div className="flex flex-col flex-1 md:min-w-0 md:gap-y-[4px] gap-y-0 justify-center items-center overflow-hidden md:rounded-lg rounded-[4px]">
            <p className="text-xxs font-semi-bold block md:hidden text-[color:var(--theme-text-muted)] mb-1">
              Kèo chấp(Toàn trận)
            </p>
            {/* FT Box */}
            <div className="h-6 flex flex-row items-center justify-center md:justify-around md:gap-x-1 gap-x-0.5 w-full min-w-0 max-w-full md:max-w-full py-[2px] px-[6px] md:py-[4px] md:px-[0px] kbv-odds-bg md:rounded-lg rounded-[4px] md:mt-0 mt-1 overflow-hidden">
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pl-[5px] pl-[2px] ${getTrendColor(homeHandicapTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {homeHandicapOdds || "-"}
                {renderTrendArrow(homeHandicapTrend)}
              </span>
              <span className="flex-1 flex items-center justify-center min-w-0 text-[10px] md:text-md font-semi-bold text-[var(--theme-text)] truncate">
                {handicapValue || "-"}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pr-[5px] pr-[2px] ${getTrendColor(awayHandicapTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {awayHandicapOdds || "-"}
                {renderTrendArrow(awayHandicapTrend)}
              </span>
            </div>
            {/* HT Box */}
            <p className="text-xxs font-semi-bold block md:hidden text-[color:var(--theme-text-muted)] mt-2 mb-1">
              Kèo chấp(Hiệp 1)
            </p>
            <div className="h-6 flex flex-row items-center justify-center md:justify-around md:gap-x-1 gap-x-0.5 w-full min-w-0 max-w-full md:max-w-full py-[2px] px-[6px] md:py-[4px] md:px-[0px] kbv-odds-bg md:rounded-lg rounded-[4px] md:mt-0 mt-1 overflow-hidden opacity-80">
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pl-[5px] pl-[2px] ${getTrendColor(homeHandicapHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {homeHandicapHalfOdds || "-"}
                {renderTrendArrow(homeHandicapHalfTrend)}
              </span>
              <span className="flex-1 flex items-center justify-center min-w-0 text-[10px] md:text-md font-semi-bold text-[var(--theme-text)] truncate">
                {handicapHalfValue || "-"}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pr-[5px] pr-[2px] ${getTrendColor(awayHandicapHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {awayHandicapHalfOdds || "-"}
                {renderTrendArrow(awayHandicapHalfTrend)}
              </span>
            </div>
          </div>

          {/* Tài/Xỉu FT/HT */}
          <div className="flex flex-col flex-1 md:min-w-0 md:gap-y-[4px] gap-y-0 justify-center items-center overflow-hidden md:rounded-lg rounded-[4px]">
            <p className="text-xxs font-semi-bold block md:hidden text-[color:var(--theme-text-muted)] mb-1">
              Tài/Xỉu(Toàn trận)
            </p>
            {/* FT Box */}
            <div className="h-6 flex flex-row items-center justify-center md:justify-around md:gap-x-1 gap-x-0.5 w-full min-w-0 max-w-full md:max-w-full py-[2px] px-[6px] md:py-[4px] md:px-[0px] kbv-odds-bg md:rounded-lg rounded-[4px] md:mt-0 mt-1 overflow-hidden">
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pl-[5px] pl-[2px] ${getTrendColor(overTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {overOdds || "-"}
                {renderTrendArrow(overTrend)}
              </span>
              <span className="flex-1 flex items-center justify-center min-w-0 text-[10px] md:text-md font-semi-bold text-[var(--theme-text)] truncate">
                {overUnderValue || "-"}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pr-[5px] pr-[2px] ${getTrendColor(underTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {underOdds || "-"}
                {renderTrendArrow(underTrend)}
              </span>
            </div>
            {/* HT Box */}
            <p className="text-xxs font-semi-bold block md:hidden text-[color:var(--theme-text-muted)] mt-2 mb-1">
              Tài/Xỉu(Hiệp 1)
            </p>
            <div className="h-6 flex flex-row items-center justify-center md:justify-around md:gap-x-1 gap-x-0.5 w-full min-w-0 max-w-full md:max-w-full py-[2px] px-[6px] md:py-[4px] md:px-[0px] kbv-odds-bg md:rounded-lg rounded-[4px] md:mt-0 mt-1 overflow-hidden opacity-80">
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pl-[5px] pl-[2px] ${getTrendColor(overHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {overHalfOdds || "-"}
                {renderTrendArrow(overHalfTrend)}
              </span>
              <span className="flex-1 flex items-center justify-center min-w-0 text-[10px] md:text-md font-semi-bold text-[var(--theme-text)] truncate">
                {overUnderHalfValue || "-"}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pr-[5px] pr-[2px] ${getTrendColor(underHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {underHalfOdds || "-"}
                {renderTrendArrow(underHalfTrend)}
              </span>
            </div>
          </div>

          {/* 1X2 FT/HT */}
          <div className="flex flex-col flex-1 md:min-w-0 md:gap-y-[4px] gap-y-0 justify-center items-center overflow-hidden md:rounded-lg rounded-[4px]">
            <p className="text-xxs font-semi-bold block md:hidden text-[color:var(--theme-text-muted)] mb-1">
              1X2(Toàn trận)
            </p>
            {/* FT Box */}
            <div className="h-6 flex flex-row items-center justify-center md:justify-around md:gap-x-1 gap-x-0.5 w-full min-w-0 max-w-full md:max-w-full py-[2px] px-[6px] md:py-[4px] md:px-[0px] kbv-odds-bg md:rounded-lg rounded-[4px] md:mt-0 mt-1 overflow-hidden">
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pl-[5px] pl-[2px] ${getTrendColor(homeEuropeTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {homeEuropeOdds || "-"}
                {renderTrendArrow(homeEuropeTrend)}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 ${getTrendColor(drawTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {instantDrawOdds || "-"}
                {renderTrendArrow(drawTrend)}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pr-[5px] pr-[2px] ${getTrendColor(awayEuropeTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {awayEuropeOdds || "-"}
                {renderTrendArrow(awayEuropeTrend)}
              </span>
            </div>
            {/* HT Box */}
            <p className="text-xxs font-semi-bold block md:hidden text-[color:var(--theme-text-muted)] mt-2 mb-1">
              1X2(Hiệp 1)
            </p>
            <div className="h-6 flex flex-row items-center justify-center md:justify-around md:gap-x-1 gap-x-0.5 w-full min-w-0 max-w-full md:max-w-full py-[2px] px-[6px] md:py-[4px] md:px-[0px] kbv-odds-bg md:rounded-lg rounded-[4px] md:mt-0 mt-1 overflow-hidden opacity-80">
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pl-[5px] pl-[2px] ${getTrendColor(homeEuropeHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {homeEuropeHalfOdds || "-"}
                {renderTrendArrow(homeEuropeHalfTrend)}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 ${getTrendColor(drawHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {drawHalfOdds || "-"}
                {renderTrendArrow(drawHalfTrend)}
              </span>
              <span className={`flex-1 flex items-center justify-center min-w-0 md:pr-[5px] pr-[2px] ${getTrendColor(awayEuropeHalfTrend)} text-[10px] md:text-md font-semi-bold truncate`}>
                {awayEuropeHalfOdds || "-"}
                {renderTrendArrow(awayEuropeHalfTrend)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 px-2 pb-2 pt-1 border-t border-[var(--theme-border)]/5">
        <button
          type="button"
          onClick={handleOpenTipsAiModal}
          className="kbv-v25-tips-ai-btn flex items-center gap-1.5 px-3 py-1 rounded-[4px] text-[var(--theme-on-primary)]"
        >
          <img src={viugoTipsAiSparkle} alt="" className="kbv-v25-tips-ai-sparkle h-3.5 w-3.5 shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-tight">Tips AI</span>
        </button>
        {match?.countOtherOdds > 1 && (
          <button
            type="button"
            onClick={handleOtherOddsClick}
            className="kbv-v25-other-bets-btn flex items-center gap-1.5 bg-[var(--theme-primary)] px-3 py-1 rounded-[4px] shadow-sm hover:scale-105 transition-all text-[var(--theme-on-primary)]"
          >
            {!showOther && (
              <span className="text-[12px] font-bold">
                {match?.countOtherOdds || 0}
              </span>
            )}
            <span className="text-[11px] font-bold uppercase tracking-tight">
              {showOther ? "Thu gọn" : "Kèo khác"}
            </span>
            <img
              src={ICON_ARROW_UP}
              alt=""
              className={`kbv-v25-other-bets-arrow block w-3 h-3 transition-transform ${showOther ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{ formattedDate: matchDateStr, formattedGio: matchTimeStr, isLive }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow,
          homeRed,
          awayYellow,
          awayRed,
          currentHomeCorner,
          currentAwayCorner,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={derivedLeagueName}
        leagueLogo={derivedLeagueLogo}
        homeName={homeName}
        homeLogo={homeLogo}
        awayName={awayName}
        awayLogo={awayLogo}
        matchDate={matchDateStr}
        matchTime={matchTimeStr}
        matchTimestamp={match?.matchTime}
        headerScore={scoreText}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={modalStatusLabel}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />

      {showOther && (
        <BetOKOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{
            handicapValue,
            homeHandicapOdds,
            awayHandicapOdds,
            homeHandicapTrend,
            awayHandicapTrend,
            overUnderValue,
            overOdds,
            underOdds,
            overTrend,
            underTrend,
            homeEuropeOdds,
            awayEuropeOdds,
            instantDrawOdds,
            homeEuropeTrend,
            awayEuropeTrend,
            drawTrend,
            handicapHalfValue,
            homeHandicapHalfOdds,
            awayHandicapHalfOdds,
            homeHandicapHalfTrend,
            awayHandicapHalfTrend,
            overUnderHalfValue,
            overHalfOdds,
            underHalfOdds,
            overHalfTrend,
            underHalfTrend,
            homeEuropeHalfOdds,
            awayEuropeHalfOdds,
            drawHalfOdds,
            homeEuropeHalfTrend,
            awayEuropeHalfTrend,
            drawHalfTrend,
          }}
          showOther={showOther}
        />
      )}
    </div>
  );
}
